// Drawer «Reemplazar logo de la empresa» / «Logo de {marca}» (handoff §2):
// una Dropzone y la vista previa «Portada de reporte», con «Guardar logo»
// deshabilitado hasta que haya un archivo que sirva. El mismo drawer para
// los dos destinos, parametrizado por `target`.
//
// Qué se acepta lo dicta el backend (PNG, JPG o WEBP de hasta 2 MB; sin
// SVG) y el handoff suma el ancho mínimo de 400 px: tipo y peso los
// rechaza la Dropzone al soltar, el ancho se mide aquí antes de mostrar
// la vista previa (./logoUpload.ts). El archivo se sube sólo al pulsar
// «Guardar logo»: presign → PUT → commit, y la pantalla muestra el toast.
//
// La vista previa «Portada de reporte» pinta lo que va a subirse —el logo
// aplanado sobre blanco y centrado con margen (lib/media/padToSquare, el
// mismo paso del cable)—, no el archivo original, para que lo que se ve
// sea lo que llega a la portada. Si el navegador no puede acolchar (sin
// canvas), se muestra el original y el cable fallará con su propio aviso.

import { useEffect, useId, useState } from "react";
import type { MyOrganizationBrand } from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import { Dropzone } from "../../../components/Dropzone";
import { formatBytes } from "../../../components/Dropzone";
import { padToSquare } from "../../../lib/media/padToSquare";
import {
  LOGO_ACCEPT_ATTR,
  LOGO_MAX_BYTES,
  LOGO_MIN_WIDTH,
  inspectLogoFile,
  type LogoTarget,
} from "./logoUpload";
import { useUploadLogo } from "./queries";

export type LogoDrawerTarget =
  | { kind: "org"; name: string; logoUrl: string | null }
  | { kind: "brand"; brand: MyOrganizationBrand };

export type LogoDrawerProps = {
  target: LogoDrawerTarget;
  onClose: () => void;
  /** Mensaje del toast que muestra la pantalla una vez cerrado el drawer. */
  onSaved: (message: string) => void;
};

const SAVED_MESSAGE = "Logo guardado · se usa en los próximos reportes";

function wireTarget(target: LogoDrawerTarget): LogoTarget {
  return target.kind === "org" ? { kind: "org" } : { kind: "brand", brandId: target.brand.id };
}

/** El archivo elegido y el blob acolchado que se muestra en la vista previa. */
type PickedLogo = { file: File; preview: Blob };

async function pickedLogoOf(file: File): Promise<PickedLogo> {
  try {
    const { blob } = await padToSquare(file);
    return { file, preview: blob };
  } catch {
    return { file, preview: file };
  }
}

export function LogoDrawer({ target, onClose, onSaved }: LogoDrawerProps) {
  const [picked, setPicked] = useState<PickedLogo | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = useUploadLogo();
  const errorId = useId();

  const currentLogoUrl = target.kind === "org" ? target.logoUrl : (target.brand.logoUrl ?? null);
  const title =
    target.kind === "org"
      ? currentLogoUrl
        ? "Reemplazar logo de la empresa"
        : "Subir logo de la empresa"
      : `Logo de ${target.brand.name}`;
  const name = target.kind === "org" ? target.name : target.brand.name;
  const file = picked?.file ?? null;

  // Vista previa del blob acolchado; la URL se libera al cambiarlo o al
  // cerrar.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!picked) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(picked.preview);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [picked]);

  async function onFiles(files: File[]) {
    const chosen = files[0];
    if (!chosen) return;
    setError(null);
    setChecking(true);
    try {
      const reason = await inspectLogoFile(chosen);
      if (reason) {
        setPicked(null);
        setError(reason);
      } else {
        setPicked(await pickedLogoOf(chosen));
      }
    } finally {
      setChecking(false);
    }
  }

  async function save() {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({ target: wireTarget(target), file });
      onSaved(SAVED_MESSAGE);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No pudimos subir el logo. Intenta de nuevo.");
    }
  }

  const busy = checking || upload.isPending;
  const shownUrl = previewUrl ?? currentLogoUrl;

  return (
    <Drawer
      title={title}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin cambiar el logo"
            disabled={upload.isPending}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={
              target.kind === "org"
                ? "Subir este archivo como logo de la empresa"
                : `Subir este archivo como logo de ${target.brand.name}`
            }
            disabled={!file || busy}
            onClick={() => void save()}
          >
            {upload.isPending ? "Subiendo…" : "Guardar logo"}
          </button>
        </>
      }
    >
      {error && (
        <p id={errorId} className="auth-error ag-mb16" role="alert">
          {error}
        </p>
      )}
      <Dropzone
        icon="image"
        title="Arrastra el logo aquí"
        hint={`PNG, JPG o WEBP con fondo transparente · mínimo ${LOGO_MIN_WIDTH} px de ancho · hasta ${formatBytes(LOGO_MAX_BYTES)}`}
        accept={LOGO_ACCEPT_ATTR}
        maxSize={LOGO_MAX_BYTES}
        disabled={busy}
        buttonTitle="Abre el selector de archivos para elegir el logo"
        onFiles={(files) => void onFiles(files)}
      />
      {file && (
        <p className="ag-muted ag-mt16">
          Archivo elegido: <span className="mono">{file.name}</span> · {formatBytes(file.size)}
        </p>
      )}
      <div className="drawer__section-label ag-mt16">Vista previa</div>
      <div className="brand-list__row">
        <div className="brand-list__preview">
          {shownUrl ? (
            <img src={shownUrl} alt="" className="brand-list__img" />
          ) : (
            <span className="cell-meta">Sin logo</span>
          )}
        </div>
        <div className="brand-list__meta">
          <div className="ag-strong">Portada de reporte</div>
          <div className="cell-strong">{name}</div>
          <div className="ag-muted">
            Así se verá sobre fondo claro. Si el logo es oscuro sobre transparente, se ve bien aquí.
          </div>
        </div>
      </div>
    </Drawer>
  );
}
